import React, { useEffect, useState } from "react";
import SEOHead from "@/components/SEOHead";
import { useSearchParams } from "react-router-dom";
import { PackageSearch, SlidersHorizontal, X } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

export default function CatalogPage() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);

  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "";
  const search = params.get("search") || "";
  const sort = params.get("sort") || "newest";
  const featured = params.get("featured") === "true";
  const bestseller = params.get("bestseller") === "true";
  const minPrice = params.get("min_price") || "";
  const maxPrice = params.get("max_price") || "";
  // SEO Phase C: slug kategori valid-bentuk tapi tidak ada → bukan halaman kategori indexable.
  const categoryInvalid = !!category && categories.length > 0 && !categories.some((c) => c.slug === category);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = { sort, limit: 50 };
    if (category) q.category = category;
    if (search) q.search = search;
    if (featured) q.featured = true;
    if (bestseller) q.bestseller = true;
    if (minPrice) q.min_price = minPrice;
    if (maxPrice) q.max_price = maxPrice;
    api.get("/products", { params: q })
      .then((r) => { setProducts(r.data.items); setTotal(r.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, search, sort, featured, bestseller, minPrice, maxPrice]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === "" || value === null) next.delete(key);
    else next.set(key, value);
    setParams(next);
  };

  const FiltersPanel = (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest brand-text-primary font-bold mb-2">Kategori</div>
        <div className="space-y-1">
          <button
            data-testid="filter-cat-all"
            onClick={() => updateParam("category", "")}
            className={`block min-h-10 w-full text-left text-sm px-3 py-2 rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-primary)] ${!category ? "color-chip-active font-bold" : "hover:bg-[color:var(--brand-background)]"}`}
          >
            Semua Kategori
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              data-testid={`filter-cat-${c.slug}`}
              onClick={() => updateParam("category", c.slug)}
              className={`block min-h-10 w-full text-left text-sm px-3 py-2 rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-primary)] ${
                category === c.slug ? "color-chip-active font-bold" : "hover:bg-[color:var(--brand-background)]"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest brand-text-primary font-bold mb-2">Rentang Harga</div>
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="number"
            placeholder="Min"
            value={minPrice}
            onChange={(e) => updateParam("min_price", e.target.value)}
            className="h-10 rounded-xl"
            data-testid="filter-min-price"
          />
          <Input
            type="number"
            placeholder="Max"
            value={maxPrice}
            onChange={(e) => updateParam("max_price", e.target.value)}
            className="h-10 rounded-xl"
            data-testid="filter-max-price"
          />
        </div>
      </div>
      <Button
        variant="outline"
        onClick={() => setParams(new URLSearchParams())}
        className="w-full rounded-xl"
        data-testid="filter-reset-btn"
      >
        <X size={14} className="mr-1" /> Reset Filter
      </Button>
    </div>
  );

  return (
    <div className="storefront-container py-6 sm:py-8 lg:py-10">
      <SEOHead title="Katalog Produk" robots={categoryInvalid ? "noindex,follow" : undefined} canonical={categoryInvalid ? "/katalog" : undefined} />
      <div className="flex items-end justify-between mb-7 sm:mb-9 flex-wrap gap-4">
        <div>
          <div className="storefront-eyebrow brand-text-primary">Katalog</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1.5">
            {search ? `Hasil "${search}"` : category ? categories.find((c) => c.slug === category)?.name || "Produk" : "Semua Produk"}
          </h1>
          <div className="text-sm text-[color:var(--brand-muted)] mt-1">{total} produk ditemukan</div>
        </div>
        <div className="flex w-full sm:w-auto items-center gap-2">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="md:hidden h-11 rounded-xl" data-testid="mobile-filter-btn">
                <SlidersHorizontal size={16} className="mr-1" /> Filter
              </Button>
            </SheetTrigger>
            <SheetContent side="left">
              <SheetHeader><SheetTitle>Filter Produk</SheetTitle></SheetHeader>
              <div className="mt-4">{FiltersPanel}</div>
            </SheetContent>
          </Sheet>
          <Select value={sort} onValueChange={(v) => updateParam("sort", v)}>
            <SelectTrigger className="h-11 min-w-0 flex-1 sm:w-[190px] sm:flex-none rounded-xl" data-testid="sort-select">
              <SelectValue placeholder="Urutkan" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Terbaru</SelectItem>
              <SelectItem value="bestseller">Terlaris</SelectItem>
              <SelectItem value="price_low">Harga Termurah</SelectItem>
              <SelectItem value="price_high">Harga Termahal</SelectItem>
              <SelectItem value="rating">Rating Tertinggi</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid md:grid-cols-[240px_minmax(0,1fr)] gap-6 lg:gap-8">
        <aside className="hidden md:block bg-[color:var(--brand-surface)] border border-[color:var(--brand-border)] rounded-[var(--storefront-card-radius)] p-5 h-fit sticky top-24 shadow-[var(--storefront-shadow-xs)]">
          {FiltersPanel}
        </aside>
        <section>
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5" aria-label="Memuat produk">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={`skeleton-${i}`} className="storefront-product-skeleton overflow-hidden">
                  <div className="aspect-square storefront-shimmer" />
                  <div className="p-3.5 sm:p-4 space-y-3">
                    <div className="h-3.5 w-10/12 rounded-full storefront-shimmer" />
                    <div className="h-3.5 w-7/12 rounded-full storefront-shimmer" />
                    <div className="h-5 w-5/12 rounded-full storefront-shimmer" />
                    <div className="h-10 w-full rounded-xl storefront-shimmer" />
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="storefront-empty-state min-h-[320px]">
              <span className="storefront-empty-icon brand-soft-bg"><PackageSearch size={26} /></span>
              <h2 className="font-display text-xl font-bold text-[color:var(--heading-text)]">Produk tidak ditemukan</h2>
              <p className="max-w-md text-sm text-[color:var(--brand-muted)]">Coba gunakan kata kunci lain atau atur ulang filter untuk melihat koleksi yang tersedia.</p>
              <Button onClick={() => setParams(new URLSearchParams())} className="system-btn-primary h-11 rounded-xl px-5 font-bold">
                Reset Filter
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
